import './paginated-table.scss';
import React, {useState} from 'react';
import {Typography, Paper, Box, useTheme, Pagination, Avatar, IconButton} from '@mui/material';
import {LoadingContent} from '../loading-content/loading-content';
import {ErrorBox} from '../error-box/error-box';
import {Link} from 'react-router-dom';
import {useContext} from 'react';
import {AppContextType} from '../../contexts/app/types';
import {AppContext, useAppContext} from '../../contexts/app/context';
import {ThemeMode} from '../../enums/theme-mode';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {PaginatedTableColumns} from '../../interfaces/paginated-table-columns';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {isSvg} from '../../utils/functions/validate-uri-svg';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {SelectInput} from '../select-input/select-input';
import Switch from '../switch/switch';
import {Button} from '../button/button';

// Parent component for a row
const RowContainer = (props: {
  linkable: boolean;
  children: React.ReactNode;
  toLink?: string;
  collapsibleContent?: React.ReactNode;
}) => {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Get theme
  const theme = useTheme();

  return (
    <>
      {props.linkable ? (
        <Link className="paginated-table-row-link" to={props.toLink || ''}>
          {props.children}
        </Link>
      ) : (
        <Box>{props.children}</Box>
      )}
      {props.collapsibleContent && (
        <Paper
          className="paginated-table-collapsible-content"
          sx={{
            backgroundColor:
              appContext.state.currentThemeMode === ThemeMode.Light
                ? theme.palette.grey[100]
                : theme.palette.background.paper,
          }}
          variant="outlined"
        >
          {props.collapsibleContent}
        </Paper>
      )}
    </>
  );
};

/**
 * Table component with pagination.
 */
type PaginatedTableProps = {
  columns: PaginatedTableColumns;
  emptyTableMessage: string;
  pagesCount: number;
  currentPage: number;
  handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  rows?: PaginatedTableData<any>;
  isLoading?: boolean;
  error?: string;
  avatar?: {
    type: 'user' | 'entity' | 'entity-service-icon' | 'other';
    isIcon?: boolean;
  };
  linkableRows?: boolean;
  collapsibleRows?: boolean;
};
export const PaginatedTable = ({
  columns,
  emptyTableMessage,
  pagesCount,
  currentPage,
  handlePageChange,
  rows,
  isLoading,
  error,
  avatar,
  linkableRows,
  collapsibleRows,
}: PaginatedTableProps) => {
  const appContext = useAppContext();
  const [openedRows, setOpenedRows] = useState<{[id: string]: string}>({});
  const theme = useTheme();

  // Open/close row
  const handleCollapsibleRow = (id: string) => {
    // Update rows
    const updatedOpenedRows = {...openedRows};

    if (updatedOpenedRows[id]) {
      // Close row
      delete updatedOpenedRows[id];
    } else {
      // Open row
      updatedOpenedRows[id] = id;
    }

    // Update rows
    setOpenedRows(updatedOpenedRows);
  };

  // Avatar sizes, coefficients and default icon
  const avatarSize: number = avatarSizesAndCoefficients.avatarSizes.giant;
  const iconSize: number = avatarSize * avatarSizesAndCoefficients.iconSizeCoefficient;
  let defaultAvatarIcon: React.ReactElement = <></>;
  let avatarBorderRadius: string | undefined;
  if (avatar) {
    switch (avatar.type) {
      case 'user':
        defaultAvatarIcon = <PersonIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
        break;
      case 'entity':
        defaultAvatarIcon = <BusinessIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
        }px`;
        break;
      case 'entity-service-icon':
        defaultAvatarIcon = <></>;
        avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
        break;
      default:
        defaultAvatarIcon = <></>;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
        }px`;
        break;
    }
  }

  return (
    <>
      <Paper
        className="paginated-table-line-container is-header"
        sx={{
          backgroundColor:
            appContext.state.currentThemeMode === ThemeMode.Light
              ? theme.palette.grey[100]
              : theme.palette.background.paper,
        }}
        variant="outlined"
      >
        {columns.map((column) => {
          return (
            <Box
              key={`table-header-column-${column.columnId}`}
              className="paginated-table-line-column"
              sx={{
                flexGrow: column.flexGrow,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  overflowWrap: 'anywhere',
                  textAlign: 'center',
                }}
                variant="body2"
                fontWeight="bold"
              >
                {column.title}
              </Typography>
            </Box>
          );
        })}
      </Paper>
      {!isLoading ? (
        <>
          {rows &&
            rows.length > 0 &&
            rows.map((row) => {
              return (
                <RowContainer
                  key={`table-row-${row.id}`}
                  linkable={linkableRows === true && row.toLink ? true : false}
                  toLink={row.toLink || ''}
                  collapsibleContent={
                    collapsibleRows === true && openedRows[row.id]
                      ? row.collapsibleRowContent
                      : undefined
                  }
                >
                  <Paper
                    className={`paginated-table-line-container ${
                      row.enabled === false ? 'is-inactive' : ''
                    } is-row`}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    variant="elevation"
                  >
                    {collapsibleRows === true && (
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          // Block navigation from parent component (RowContainer)
                          event?.stopPropagation();
                          event?.preventDefault();

                          // Close/open row
                          handleCollapsibleRow(row.id);
                        }}
                      >
                        {openedRows[row.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                    {columns.map((column, index) => {
                      // Key name
                      const key: string = `table-row-${row.id}-column-${column.columnId}`;

                      // Column values
                      const columnValues = row.columns.find(
                        (obj) => obj.columnId === column.columnId
                      );

                      // Column value
                      const value: string = columnValues?.value || '';

                      // Column avatar
                      const columnAvatar: string | undefined = columnValues?.avatarUrl;

                      // Show column value
                      let showValue: boolean =
                        (!column.select && !column.button && !column.chip) ||
                        (column.button && column.button.showColumnValue)
                          ? true
                          : false;

                      // Toggle button
                      const hasToggleButton: boolean =
                        column.button && column.button.type === 'toggle' && column.button.toggle
                          ? true
                          : false;

                      // Icon button
                      let hasIconButton: boolean = false;
                      if (
                        column.button &&
                        column.button.type === 'icon-button' &&
                        column.button.iconButton
                      ) {
                        if (column.button.iconButton.showOnValues) {
                          if (column.button.iconButton.showOnValues.includes(value)) {
                            hasIconButton = true;
                          } else {
                            hasIconButton = false;
                          }
                        } else {
                          hasIconButton = true;
                        }
                      }

                      // Text button
                      let hasTextButton: boolean = false;
                      if (
                        column.button &&
                        column.button.type === 'text-button' &&
                        column.button.textButton
                      ) {
                        if (column.button.textButton.showOnValues) {
                          if (column.button.textButton.showOnValues.includes(value)) {
                            hasTextButton = true;
                          } else {
                            hasTextButton = false;
                          }
                        } else {
                          hasTextButton = true;
                        }

                        // Check if we should show the column value with the button
                        if (
                          hasTextButton &&
                          column.button.textButton.hideColumnValueWhenShown === true
                        ) {
                          showValue = false;
                        }
                      }

                      // Select
                      let hasSelect: boolean = false;
                      if (column.select) {
                        hasSelect = true;
                      }

                      // Chip
                      let hasChip: boolean = false;
                      if (column.chip === true) {
                        hasChip = true;
                      }

                      return (
                        <React.Fragment key={key}>
                          {avatar && index === 0 ? (
                            <Box
                              className="paginated-table-line-column"
                              sx={{
                                flexGrow: column.flexGrow,
                              }}
                            >
                              {avatar?.isIcon ? (
                                <Avatar
                                  className="paginated-table-avatar"
                                  sx={{
                                    width: avatarSize,
                                    height: avatarSize,
                                    borderRadius: avatarBorderRadius,
                                    borderColor: theme.palette.action.disabled,
                                    backgroundColor: row.avatarBackgroundColor
                                      ? row.avatarBackgroundColor
                                      : undefined,
                                  }}
                                >
                                  {row.avatarUrl && row.avatarUrl.trim().length !== 0 ? (
                                    isSvg(row.avatarUrl) ? (
                                      <object
                                        className="paginated-table-avatar-svg-object"
                                        data={row.avatarUrl}
                                        aria-label={value}
                                        type="image/svg+xml"
                                        width={`${iconSize}`}
                                        height={`${iconSize}`}
                                      ></object>
                                    ) : (
                                      <img
                                        style={{
                                          width: `${iconSize}px`,
                                          height: `${iconSize}px`,
                                        }}
                                        src={row.avatarUrl}
                                        alt={value}
                                      />
                                    )
                                  ) : (
                                    defaultAvatarIcon
                                  )}
                                </Avatar>
                              ) : (
                                <Avatar
                                  className="paginated-table-avatar"
                                  src={row.avatarUrl ? row.avatarUrl : undefined}
                                  alt={value}
                                  sx={{
                                    width: avatarSize,
                                    height: avatarSize,
                                    borderRadius: avatarBorderRadius,
                                    borderColor: theme.palette.action.disabled,
                                    backgroundColor: row.avatarBackgroundColor
                                      ? row.avatarBackgroundColor
                                      : undefined,
                                  }}
                                >
                                  {defaultAvatarIcon}
                                </Avatar>
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  paddingLeft: '14px',
                                  overflowWrap: 'anywhere',
                                  textAlign: 'left',
                                }}
                              >
                                {value}
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              className="paginated-table-line-column"
                              sx={{
                                flexGrow: column.flexGrow,
                                justifyContent: index !== 0 ? 'center' : undefined,
                              }}
                            >
                              {hasToggleButton && !hasSelect && !hasChip && (
                                <Switch
                                  value={
                                    value === column.button?.toggle?.enabledValue ? true : false
                                  }
                                  size="medium"
                                  color="primary"
                                  onClick={(event) => {
                                    // Block navigation from parent component (RowContainer)
                                    event?.stopPropagation();
                                    event?.preventDefault();

                                    // Block event if disabled
                                    if (column.button?.disabled) return;

                                    // Do something
                                    (column.button as any).toggle.handleClick(
                                      row.id,
                                      value === (column.button as any).toggle.enabledValue
                                        ? false
                                        : true
                                    );
                                  }}
                                  disabled={column.button?.disabled}
                                  disabledTooltip={column.button?.disabledTooltip}
                                  sx={{marginRight: '12px'}}
                                />
                              )}
                              {hasIconButton && !hasSelect && !hasChip && (
                                <Box>
                                  <IconButton
                                    color={(column.button as any).iconButton.iconColor}
                                    component="span"
                                    onClick={(event: any) => {
                                      // Block navigation from parent component (RowContainer)
                                      event?.stopPropagation();
                                      event?.preventDefault();

                                      // Do something
                                      (column.button as any).iconButton.handleClick(row.id);
                                    }}
                                    disabled={column.button?.disabled}
                                    sx={{
                                      pointerEvents: column.button?.disabled ? 'all' : 'auto',
                                      cursor: column.button?.disabled ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {(column.button as any).iconButton.icon}
                                  </IconButton>
                                </Box>
                              )}
                              {hasTextButton && !hasSelect && !hasChip && (
                                <Button.Tooltip
                                  variant="outlined"
                                  color={(column.button as any).textButton.color}
                                  onClick={(event: any) => {
                                    // Block navigation from parent component (RowContainer)
                                    event?.stopPropagation();
                                    event?.preventDefault();

                                    // Do something
                                    (column.button as any).textButton.handleClick(row.id);
                                  }}
                                  disabled={column.button?.disabled}
                                  disabledTooltip={column.button?.disabledTooltip}
                                >
                                  {(column.button as any).textButton.label}
                                </Button.Tooltip>
                              )}
                              {hasSelect && !hasChip && (
                                <SelectInput
                                  value={value}
                                  items={
                                    column.select?.items as Array<{
                                      value: string;
                                      label: string;
                                    }>
                                  }
                                  inputSize="small"
                                  handleClose={(event) => {
                                    // Block navigation from parent component (RowContainer)
                                    event?.stopPropagation();
                                    event?.preventDefault();
                                  }}
                                  handleChange={(newValue, event) => {
                                    // Block navigation from parent component (RowContainer)
                                    event?.stopPropagation();
                                    event?.preventDefault();

                                    // Do something
                                    if (newValue && newValue !== value) {
                                      (column.select as any).handleChange(row.id, newValue);
                                    }
                                  }}
                                  disabled={column.button?.disabled}
                                />
                              )}
                              {hasChip && value && (
                                <Box
                                  className="paginated-table-chip"
                                  sx={{
                                    backgroundColor: theme.palette.action.selected,
                                  }}
                                >
                                  <Avatar
                                    alt={value}
                                    src={columnAvatar}
                                    sx={{
                                      color:
                                        appContext.state.currentThemeMode === ThemeMode.Dark
                                          ? '#e0e0e0'
                                          : '#616161',
                                      width: `${avatarSizesAndCoefficients.avatarSizes.small}px`,
                                      height: `${avatarSizesAndCoefficients.avatarSizes.small}px`,
                                    }}
                                  >
                                    <PersonIcon
                                      sx={{
                                        width: `${
                                          avatarSizesAndCoefficients.avatarSizes.small *
                                          avatarSizesAndCoefficients.iconSizeCoefficient
                                        }px`,
                                        height: `${
                                          avatarSizesAndCoefficients.avatarSizes.small *
                                          avatarSizesAndCoefficients.iconSizeCoefficient
                                        }px`,
                                      }}
                                    />
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    className="paginated-table-chip-value"
                                  >
                                    {value}
                                  </Typography>
                                </Box>
                              )}
                              {showValue && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflowWrap: 'anywhere',
                                    textAlign: index === 0 ? 'left' : 'center',
                                  }}
                                >
                                  {value}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Paper>
                </RowContainer>
              );
            })}
          {(!rows || (rows && rows.length === 0)) && !error ? (
            <Box className="paginated-table-empty-message">
              <Typography textAlign="center" variant="body2" fontWeight="bold">
                {emptyTableMessage}
              </Typography>
            </Box>
          ) : undefined}
          {error ? (
            <Box className="paginated-table-error-message">
              <ErrorBox error={error} />
            </Box>
          ) : undefined}
        </>
      ) : (
        <LoadingContent marginTop="24px" />
      )}
      {pagesCount > 1 ? (
        <Box className="paginated-table-footer-pagination">
          <Pagination
            count={pagesCount}
            page={currentPage}
            onChange={(event: React.ChangeEvent<unknown>, value: number) => {
              if (collapsibleRows === true) {
                setOpenedRows({});
              }
              handlePageChange(event, value);
            }}
          />
        </Box>
      ) : undefined}
    </>
  );
};
